import React, { Component, HTMLAttributes } from "react";
import * as PropTypes from "prop-types";
import { inject, observer } from "mobx-react";
import Scrollbar from "react-scrollbars-custom";
import cx from "classnames";
import moment from "moment";
import ThumbnailContainer from "pages/viewer-dashboard/container/seriesExplorer/thumbnailContainer";

import Controller from "pages/viewer-dashboard/section/studyBrowserSection/controller";
import Cache from "core/cache";
import FloatMessageBox from "core/messageBoxController";
import Constants from "constants/index";
import {
  redrawAllEnabledElement,
  getEnabledElementById,
} from "component/toolManager/enabledElements.js";
import { clearAll } from "component/toolManager/toolState";
import "./studyBrowserSection.styl";
import "./customScroll.styl";
import Config from "config";
/**
 * @description StudyBrowserSection UI Component Properties
 *
 * @interface IStudyBrowserSectionProps
 * @extends {HTMLAttributes<HTMLDivElement>}
 */
interface IStudyBrowserSectionProps extends HTMLAttributes<HTMLDivElement> {
  gridLayoutStore: any;
  synchronizationStore: any;
  metadataStore: any;
  seriesListStore: any;
  /**
   * @description Y 축 Scroll이 데이터가 없어도 보일것인지 선택 파라미터
   */
  permanentTrackX: boolean;
  /**
   * @description X 축 Scroll이 데이터가 없어도 보일것인지 선택 파라미터
   */
  permanentTrackY: boolean;
  /**
   * @description X 축 스크롤 유/무
   */
  noScrollX: boolean;
  /**
   * @description Y 축 스크롤 유/무
   */
  noScrollY: boolean;
}

/**
 * @description StudyBrowserSection UI Component States
 *
 * @interface IStudyBrowserSectionState
 */
interface IStudyBrowserSectionState {}

/**
 * @description StudyBrowserSection UI Class Component
 *
 * @class StudyBrowserSection
 * @extends {Component<IStudyBrowserSectionProps, any>}
 */
@inject("toolbarStore")
@inject("gridLayoutStore")
@inject("metadataStore")
@inject("seriesListStore")
@inject("synchronizationStore")
@observer
class StudyBrowserSection extends Component<
  IStudyBrowserSectionProps,
  IStudyBrowserSectionState
> {
  static propTypes: PropTypes.InferProps<IStudyBrowserSectionProps>;

  /**
   * default property values
   *
   * @static
   * @memberof StudyBrowserSection
   */
  public static defaultProps = {
    permanentTrackX: false,
    permanentTrackY: true,
    noScrollX: true,
    noScrollY: false,
  };

  /**
   * @method handlerRemoveOneData
   * @description 데이터 모델객체에 한개 시리즈 데이터 삭제를 처리하는 메세지 핸들러
   * @memberof StudyBrowserSection
   */
  handleRemoveOneData = (seriesID: any, msg: any) => {
    const { metadataStore, gridLayoutStore, synchronizationStore } = this.props;

    const parameters = {
      title: "Warning",
      message: Constants.Strings.DialogMessage.ConfirmRemoveDataOnDCMView,
      buttonType: "YESNO",
      hasCloseButton: false,
      hasTodayOffButton: true,
      handleYesClick: function () {
        let metadataCache = new Cache.Metadata();
        const seriesInstanceUID = metadataCache.getSeriesInstanceUID(seriesID);
        const modality = metadataCache.getModality(seriesID);
        const seriesInstanceKey = Cache.KeyGen.makeKey(
          seriesInstanceUID,
          modality
        );
        gridLayoutStore.remove(seriesInstanceKey);

        gridLayoutStore.setLayoutTotalPages(1);
        metadataStore.delete(seriesInstanceKey);
        if (
          synchronizationStore.isSupportedSynchronizationOnActive() === true
        ) {
          synchronizationStore.reactiveSyncState();
        } else {
          synchronizationStore.disableSyncState();
        }
      },
    };
    FloatMessageBox(parameters);
  };

  /**
   * @method handlerRemoveAllData
   * @description 데이터 모델객체에 전체 시리즈 데이터 삭제를 처리하는 메세지 핸들러
   * @memberof StudyBrowserSection
   */
  handlerRemoveAllData = () => {
    const { metadataStore, gridLayoutStore, synchronizationStore } = this.props;
    const parameters = {
      title: "Warning",
      message: Constants.Strings.DialogMessage.ConfirmRemoveAllDataOnDCMView,
      buttonType: "YESNO",
      hasCloseButton: false,
      hasTodayOffButton: false,
      handleYesClick: function () {
        ///
        /// 데이터 삭제 메세지 전달
        ///
        gridLayoutStore.clearAll();
        clearAll();
        redrawAllEnabledElement();
        gridLayoutStore.setLayoutTotalPages(1);
        metadataStore.clear();
        if (
          synchronizationStore.isSupportedSynchronizationOnActive() === true
        ) {
          synchronizationStore.reactiveSyncState();
        } else {
          synchronizationStore.disableSyncState();
        }
      },
    };
    FloatMessageBox(parameters);
  };
  handleClick = (event: MouseEvent, metadataID: string) => {
    const { metadataStore, gridLayoutStore } = this.props;
    ///
    /// 1. 활성화된 viewport의 index 획득
    ///
    if (Config.control.AllocateAllFramesOnLayout === false) {
      const activeDCMViews = gridLayoutStore.getActiveDCMViews();
      if (activeDCMViews.length === 0) {
        return;
      }
      const activeDCMViewIndex = activeDCMViews[0];
      //const activeDCMViewIndex = gridLayoutStore.getActiveDCMViewIndex();
      if (activeDCMViewIndex === -1) {
        return;
      }
      const enabledElement = getEnabledElementById(activeDCMViewIndex);
      if (!!enabledElement === false) {
        return;
      }
      ///
      /// 2. 데이터 출력 요청
      ///
      Controller.ReadyRequestDisplayData(
        activeDCMViewIndex,
        enabledElement,
        metadataID,
        0,
        false
      );
    } else {
      var startTime = performance.now();
      Config.startTime = startTime;
      // 1. 페이지 개수 계산
      const numOfFrames = metadataStore.getNumberOfFrames(metadataID);
      const numOfViews =
        gridLayoutStore.getLayoutRows() * gridLayoutStore.getLayoutColumns();
      const alpha = numOfFrames % numOfViews > 0 ? 0 : 1;
      //const numOfPages = numOfFrames / numOfViews + alpha;
      const numOfPages = Math.ceil(numOfFrames / numOfViews) + alpha;

      // 2. 페이지 설정
      gridLayoutStore.setLayoutTotalPages(numOfPages);
      gridLayoutStore.focusOneLayout(-1);
      // 3. DCM View 슬라이스 일괄 출력 요청 정보 정의
      let requestView = [];
      // 실험용 코드
      for (let i = 0; i < numOfFrames; i++) {
        requestView.push({
          DCMViewIdx: i,
          active: i === 0 ? true : false,
          MetadataUID: metadataID,
          frameIdx: i,
          //frameIdx: i * eachFrames,
          BatchMode: true,
          imageOrientation: "axial",
          showTagOverlay: true,
        });
      }
      // for (let i = 0; i < numOfFrames; i++) {
      //   requestView.push({
      //     DCMViewIdx: i,
      //     active: i === 0 ? true : false,
      //     MetadataUID: metadataID,
      //     frameIdx: i,
      //     BatchMode: true,
      //   });
      // }

      /// 4. 현재 DCMView에서 그리기 정보 모두 삭제
      clearAll();
      /// 5. 일괄 출력 요청
      gridLayoutStore.setMetadataOnViewAsList(requestView);
    }
  };
  // handleClick = (event: MouseEvent, metadataID: string) => {
  //   const { metadataStore, gridLayoutStore } = this.props;
  //   ///
  //   /// 1. 활성화된 viewport의 index 획득
  //   ///
  //   if (Config.control.AllocateAllFramesOnLayout === false) {
  //     const activeDCMViewIndex = gridLayoutStore.getActiveDCMViewIndex();
  //     if (activeDCMViewIndex === -1) {
  //       return;
  //     }
  //     const enabledElement = getEnabledElementById(activeDCMViewIndex);
  //     if (!!enabledElement === false) {
  //       return;
  //     }
  //     ///
  //     /// 2. 데이터 출력 요청
  //     ///
  //     Controller.ReadyRequestDisplayData(
  //       activeDCMViewIndex,
  //       enabledElement,
  //       metadataID,
  //       0,
  //       false
  //     );
  //   } else {
  //     /// 1. 페이지 개수 계산
  //     const numOfFrames = metadataStore.getNumberOfFrames(metadataID);
  //     const numOfViews =
  //       gridLayoutStore.getLayoutRows() * gridLayoutStore.getLayoutColumns();
  //     const alpha = numOfFrames % numOfViews > 0 ? 0 : 1;
  //     //const numOfPages = numOfFrames / numOfViews + alpha;
  //     const numOfPages = Math.ceil(numOfFrames / numOfViews) + alpha;

  //     /// 2. 페이지 설정
  //     gridLayoutStore.setLayoutTotalPages(numOfPages);

  //     /// 3. DCM View 슬라이스 일괄 출력 요청 정보 정의
  //     let requestView = [];
  //     for (let i = 0; i < numOfFrames; i++) {
  //       requestView.push({
  //         DCMViewIdx: i,
  //         active: i === 0 ? true : false,
  //         MetadataUID: metadataID,
  //         frameIdx: i,
  //         BatchMode: true,
  //       });
  //     }

  //     /// 4. 현재 DCMView에서 그리기 정보 모두 삭제
  //     clearAll();
  //     /// 5. 일괄 출력 요청
  //     gridLayoutStore.setMetadataOnViewAsList(requestView);
  //   }
  // };

  handleDoubleClick = (event: MouseEvent, metadataID: string) => {
    const { gridLayoutStore } = this.props;
    ///
    /// 0. 현재 레이아웃 배열 확인
    ///
    const layoutRows = gridLayoutStore.getLayoutRows();
    const layoutColumns = gridLayoutStore.getLayoutColumns();

    ///
    /// 1. 레이아웃이 1x1 인지, 아닌지에 따라 구분하여 데이터 표시 동작
    ///

    if (layoutRows === 1 && layoutColumns === 1) {
      ///
      /// 1-A. 레이아웃이 1x1이라면, 사용자 선택에 따라 출력 데이터 교체
      ///

      const viewportElement = getEnabledElementById(0);
      //Controller.ReadyRequestDisplayData(0, viewportElement, metadataID);
      Controller.ReadyRequestDisplayData(
        0,
        viewportElement,
        metadataID,
        0,
        false
      );
    } else {
      ///
      /// 1-B. 레이아웃이 1x1이 아니라면, 빈공간에 데이터 출력
      ///
      const emptyViewportIndex = gridLayoutStore.getEmptyDCMViewIndex();
      if (emptyViewportIndex === -1) {
        return;
      }
      const viewportElement = getEnabledElementById(emptyViewportIndex);
      Controller.ReadyRequestDisplayData(
        emptyViewportIndex,
        viewportElement,
        metadataID,
        0,
        false
      );

      return;
    }
  };

  /**
   * @method renderSeriesThumbnail
   * @description DICOM Thumbnail UI를 출력
   * @memberof StudyBrowserSection
   * @return dateAsMoment.form/../at(studyDate);
   */
  renderDICOMThumbnail = (seriesSpec: any, index: any) => {
    const { seriesListStore } = this.props;
    const activeSeriesID = seriesListStore.getActiveSeriesItem();
    const seriesID = Cache.KeyGen.makeKey(
      seriesSpec.seriesInstanceUID,
      seriesSpec.modality
    );
    let isActive = false;
    if (seriesID === activeSeriesID) {
      isActive = true;
    }

    //
    return (
      <ThumbnailContainer
        key={seriesID}
        metadataID={seriesID}
        index={index}
        metadataSpec={seriesSpec}
        active={isActive}
        onClick={this.handleClick}
        onDblClick={this.handleDoubleClick}
        onDelete={this.handleRemoveOneData}
        onClear={this.handlerRemoveAllData}
      />
    );
  };

  /**
   * @method render
   * @description StudyBrowserSection UI 컴포넌트 렌더링
   * @returns
   * @memberof StudyBrowserSection
   */
  render() {
    const { noScrollX, permanentTrackY, metadataStore } = this.props;
    const seriesGroup = metadataStore.getGroupByStudyDate();
    let seriesGroupKey = Object.keys(seriesGroup).sort();
    let idx = 0;
    return (
      <div className={cx("scrollableStudyThumbnails", "nodrag", "noselect")}>
        <Scrollbar
          download=""
          inlist=""
          noScrollX={noScrollX}
          noDefaultStyles
          permanentTrackY={permanentTrackY}
          onContextMenu={(event) => event.preventDefault()}
        >
          {seriesGroupKey.map((key: any, index: any) => {
            /// map 대신 forEach를 쓰면 출력이안됨..
            const studyDate = moment(key, "YYYY-MM-DD")
              .locale("ko")
              .format("YYYY-MM-DD");
            return (
              <React.Fragment key={index}>
                <div className="study-date">{studyDate}</div>
                {seriesGroup[key].map(
                  (seriesData: any, seriesIndex: number) => {
                    return this.renderDICOMThumbnail(seriesData, idx++);
                  }
                )}
              </React.Fragment>
            );
          })}
        </Scrollbar>
      </div>
    );
  }
}

export default StudyBrowserSection;
